<script setup>
import axios from 'axios';
import {onMounted, ref} from 'vue';
import {format} from 'date-fns';
import {ru} from 'date-fns/locale';
import NewUserModal from '@/components/modals/NewUserModal.vue';
import store from '@/store/store';

// Reactive state
const users_employee = ref([]);
const session_user = ref(store.getters['profile/username']);
const isLoading = ref(false);
const error = ref(null);

// Fetch users from the API
const fetchUsers = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const res = await axios.get('accounts_list/');
    users_employee.value = res.data.map((employee) => ({
      id: employee.user.id,
      username: employee.user.username,
      first_name: employee.user.first_name,
      last_name: employee.user.last_name,
      email: employee.user.email,
      is_active: employee.user.is_active,
      last_login: employee.user.last_login
          ? format(new Date(employee.user.last_login), 'dd/MM/yyyy, HH:mm', {locale: ru})
          : '-',
      date_joined: employee.user.date_joined
          ? format(new Date(employee.user.date_joined), 'dd/MM/yyyy, HH:mm', {locale: ru})
          : '',
      is_superuser: employee.user.is_superuser,
      is_staff: employee.user.is_staff,
      is_fitter: employee.is_fitter,
      is_manager: employee.is_manager,
      is_engineer: employee.is_engineer
    }));
  } catch (err) {
    error.value = 'Failed to fetch users. Please try again later.';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

// Enable or disable a user
const toggleUserStatus = async (user_id, is_active) => {
  try {
    await axios.patch('account/', {user_id});
    const user = users_employee.value.find((u) => u.id === user_id);
    if (user) {
      user.is_active = !is_active;
    }
  } catch (err) {
    console.error('Failed to update user status:', err);
    alert('Failed to update user status. Please try again.');
  }
};

// Fetch users on component mount
onMounted(() => {
  fetchUsers();
});
</script>

<template>
  <div class="content-wrapper">
    <div class="mt-5 pt-3">
      <!-- New User Modal -->
      <new-user-modal @user-created="fetchUsers"/>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>

      <!-- Users Table -->
      <div style="overflow-x: auto;">
        <table class="table table-sm table-bordered" >
          <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">ФИО</th>
            <th scope="col">Имя пользователя</th>
            <th scope="col">Email</th>
            <th scope="col">Активный</th>
            <th scope="col">Роль</th>
            <th scope="col">Последняя активность</th>
            <th scope="col">Дата регистрации</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="employee in users_employee" :key="employee.id">
            <td>{{ employee.id }}</td>
            <td>{{ employee.first_name }} {{ employee.last_name }}</td>
            <td>{{ employee.username }}</td>
            <td>{{ employee.email }}</td>
            <td>
              <button
                  :class="[
                  'btn',
                  employee.is_active ? 'btn-success' : 'btn-danger',
                  (session_user === employee.username || employee.is_superuser) && 'disabled',
                ]"
                  :disabled="session_user === employee.username || employee.is_superuser"
                  @click="toggleUserStatus(employee.id, employee.is_active)"
              >
                <i :class="employee.is_active ? 'bi bi-check' : 'bi bi-x'"></i>
              </button>
            </td>
            <td>
              <span v-if="employee.is_superuser" class="badge bg-danger">Админ</span>
              <span v-else-if="employee.is_engineer" class="badge bg-cyan">Инженер</span>
              <span v-else-if="employee.is_manager" class="badge bg-warning">Менеджер</span>
              <span v-else-if="employee.is_fitter" class="badge bg-primary">Монтёр</span>
            </td>
            <td>{{ employee.last_login }}</td>
            <td>{{ employee.date_joined }}</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<style scoped>
.table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.badge {
  font-size: 0.9em;
  padding: 0.5em 0.75em;
}

.btn {
  min-width: 80px;
}

</style>